import { asNoteId, asOrderId, asOrderSetId, asVisitId, type NoteId, type OrderSetId, type VisitId } from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";
import { practitionerRefSchema } from "./practitioner.schemas";

// === Visit Booking Schemas ===

export const visitIdSchema = z.string().min(1).transform(asVisitId) satisfies z.ZodType<VisitId, z.ZodTypeDef, string>;

export const visitIdInputSchema = z.object({
  visitId: visitIdSchema,
});
export type VisitIdInput = z.infer<typeof visitIdInputSchema>;

// === Curated Session Booking Schemas ===

export const draftVisitNoteInputSchema = z.object({
  id: newEntityIdSchema,
  visitId: visitIdSchema,
  text: z.string(),
  // Spelled by the client: the date in it is the clinic's day, which the server's UTC is not.
  title: z.string().optional(),
});
export type DraftVisitNoteInput = z.infer<typeof draftVisitNoteInputSchema>;
// Full intent: the router adds the signed-in practitioner as the author.
export const draftVisitNoteIntentSchema = draftVisitNoteInputSchema.extend({ assessor: practitionerRefSchema });
export type DraftVisitNoteIntent = z.infer<typeof draftVisitNoteIntentSchema>;

export const updateVisitNoteInputSchema = z.object({
  noteId: z.string().min(1).transform(asNoteId) satisfies z.ZodType<NoteId, z.ZodTypeDef, string>,
  text: z.string(),
});
export type UpdateVisitNoteInput = z.infer<typeof updateVisitNoteInputSchema>;

// MIN-214 (S3), Decision A: addressed by the order (ServiceRequest id)
// directly — Task ids are unstable (the work-item producer retires/re-mints
// them) so nothing may route by them. The `{taskId}` address is removed
// entirely — no union, no callers left after this PR.
const orderSetIdSchema = z.string().min(1).transform(asOrderSetId) satisfies z.ZodType<
  OrderSetId,
  z.ZodTypeDef,
  string
>;
export const cancelVisitProcedureInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: z.string().min(1).transform(asOrderId),
});
export type CancelVisitProcedureInput = z.infer<typeof cancelVisitProcedureInputSchema>;
